import { useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from '@reach/router';
import axios, { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import { stringifyUrl } from 'query-string';
import { T, all, always, cond, equals, find, includes, or, propEq, toUpper } from 'ramda';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAsync, useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import Skeleton from '@ui/atoms/Skeleton';
import SubheadingText from '@ui/atoms/SubheadingText';
import TextButton from '@ui/atoms/TextButton';
import Toast from '@ui/atoms/Toast';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import MessageIconCard from '@ui/molecules/MessageIconCard';
import ReadMore from '@ui/molecules/ReadMore';
import SubmitButton from '@ui/molecules/SubmitButton';

import PastCancelDateAppointmentError from '@ui/routes/Dashboard/Appointments/RescheduleAppointment/PastCancelDateAppointmentError.component';
import { mentalHealthTopicConstants } from '@ui/routes/MemberMH/MemberMHTopicSelectionStep';

import aligned from '@ui/styled/Aligned';
import padded from '@ui/styled/Padded';
import spaced from '@ui/styled/Spaced';

import useMemberInfo, { SubscriptionInfo } from '@hooks/useMemberInfo';
import { useMemberIsVisitWaivable } from '@hooks/useMemberIsVisitWaivable';

import { showAppointmentToast } from '@helpers/Appointments/AppointmentHelpers';
import { getServiceType } from '@helpers/MentalHealth/mentalHealth';

import { interpolate } from '@utils/butter';
import { commonLabels } from '@utils/button';
import { ApiEndpoints } from '@utils/endpoints';
import { doesNotExist, exists } from '@utils/general';

import { getAuthToken } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import mhTopics, { MHTopic } from '@data/mh_topics';

import { IAddressInfoQueryData } from '@folx-types/address';
import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import NewAppointmentBooking, {
	ServiceDuration,
	ServiceType,
} from '@components/NewAppointmentBooking';
import { durationRequiredServiceTypes } from '@components/NewAppointmentBooking/NewAppointmentBooking.component';

import Clock from '@assets/vectors/clock.inline.svg';

import { membership, membershipName } from '@graphql/fragments/membership';
import COMPLETE_FLOW from '@graphql/mutations/completeFlowMutation.gql';
import ADDRESS_INFO_QUERY from '@graphql/queries/AddressInfo.query.gql';
import CURRENT_MEMBER_WITH_DEFAULT_VISIT_DURATION from '@graphql/queries/currentMemberWithDefaultVisitDuration.gql';

import theme from '@theme/FolxTheme';

export interface IMemberAppointmentSelectionStepPageContent {
	appointmentHeader: string;
	appointmentSubheader: string;
	appointmentDescription: string;
	appointmentSelectionHeader: string;
	appointmentSelectionDescription: string;
	medicationDescription: string;
	inPersonHeader: string;
	inPersonDescription: string;
	locationSubheader: string;
	locationDescription: string;
	cancellationHeader: string;
	cancellationDescription: string;
	editLabel: string;
	interpreterInfo: string;
	pastMembershipCancelDate: {
		cta: string;
		description: string;
		header: string;
		secondaryCta: string;
		subheader: string;
	};
}

interface IMemberAppointmentSelectionStepProps extends IFlowStepComponentProps {
	flowId: string;
	pageContent: IMemberAppointmentSelectionStepPageContent;
}

interface IScheduledAppointment {
	id: string;
	time: DateTime;
	justScheduled: boolean;
}

interface IGetBookingLocationTypeResponse {
	locationType: string;
}

const SpacedGridItem = spaced(CenteredGridItem);
const SpacedHeader = spaced(HeadingText);
const SpacedColumn = spaced<IFlexColumn>(FlexColumn);
const AlignedColumn = aligned<IFlexColumn>(FlexColumn);
const SpacedWYSIWYG = spaced(WYSIWYGContent);
const PaddedSpacedWYSIWYG = padded(SpacedWYSIWYG);

const CancellationPolicy = spaced(styled(ReadMore)`
	padding: ${theme.spacing.smmd};
`);

const BookingContainer = styled.div`
	height: 78rem;
`;

const MemberAppointmentSelectionStep: FC<IMemberAppointmentSelectionStepProps> = ({
	existingFormData = {},
	flowId,
	pageContent,
	stepKey,
}) => {
	// State variables
	const [submitting, setSubmitting] = useState(false);
	const [locationType, setLocationType] = useState<string>('');
	const [shouldResetAppointment, setShouldResetAppointment] = useState(false);
	const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(true);

	// Initial setup
	const {
		handleFormSubmission,
		handleNavBack,
		isPaymentWaived,
		setIsPaymentWaived = () => {
			return;
		},
	} = useContext(FlowContext);

	const location = useLocation();
	const { loading: memberLoading, memberInfo } = useMemberInfo<SubscriptionInfo>([
		{ name: membershipName, node: membership },
	]);
	const [markFlowComplete, { error: markCompleteError }] = useMutation(COMPLETE_FLOW);

	const {
		loading: addressLoading,
		data: addressData,
		error: addressError,
	} = useQuery<IAddressInfoQueryData, { memberId: string | undefined }>(ADDRESS_INFO_QUERY, {
		skip: !memberInfo,
		variables: {
			memberId: memberInfo?.id,
		},
	});

	// Appointment setup
	const scheduledAppointment: IScheduledAppointment | undefined = useMemo(() => {
		if (['cogsworthId', 'cogsworthAppointmentTime'].every((key) => key in existingFormData)) {
			setShouldResetAppointment(false);

			return {
				time: DateTime.fromISO(existingFormData.cogsworthAppointmentTime as string),
				id: existingFormData.cogsworthId as string,
				justScheduled: false,
			};
		}
	}, [existingFormData, existingFormData?.cogsworthId, existingFormData?.cogsworthAppointmentTime]);

	const { value: isValidAppointment, loading: appointmentLoading } = useAsync(async () => {
		if (scheduledAppointment) {
			const authorizationToken = await getAuthToken();
			const res = await axios
				.get(`${ApiEndpoints.cogsworthValidateAppointment}/${scheduledAppointment?.id}`, {
					headers: {
						authorizationToken,
					},
				})
				.catch(() => {
					return { data: { isValid: false } };
				});

			return res?.data?.isValid;
		}
	}, [scheduledAppointment]);

	const appointmentDate = scheduledAppointment?.time;
	const localizedDate = appointmentDate?.toFormat('EEEE, MMMM d');
	const localizedTime = appointmentDate?.toFormat('h:mm a ZZZZ');

	const isIrlAppointment = locationType === 'irl';
	const state = existingFormData?.shippingState ?? addressData?.addressByMemberIdAndType?.state;
	const zip = existingFormData?.shippingZip ?? addressData?.addressByMemberIdAndType?.zip;

	const isAppointmentScheduled =
		scheduledAppointment &&
		exists(scheduledAppointment.id) &&
		isValidAppointment &&
		!shouldResetAppointment;

	// Form setup
	const schema = z.object({});
	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			...(existingFormData as unknown as Schema),
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		reset,
		formState: { errors },
	} = form;

	// Service Type
	const mhTopic = existingFormData?.[mentalHealthTopicConstants.TOPIC_ID] as unknown as
		| MHTopic
		| undefined;

	const serviceType =
		flowId === 'mh'
			? getServiceType(mhTopic, {
					vcFlow: true,
					uppercase: true,
					style: mhTopic === MHTopic.MENTAL_HEALTH_LETTER ? 'underscored' : 'abbreviated',
			  }) ?? ''
			: toUpper(flowId) ?? '';
	const appointmentType =
		flowId === 'mh'
			? getServiceType(mhTopic, {
					vcFlow: true,
			  }) ?? ''
			: flowId ?? '';

	const flowKey: ServiceType = cond([
		[equals('hrt_e'), always('e')],
		[equals('hrt_t'), always('t')],
		[T, always(appointmentType)],
	])(appointmentType);

	const getMentalHealthAppointmentText = (topic?: MHTopic) => {
		const mhTopic = find(propEq('id', topic), mhTopics);
		return mhTopic?.name;
	};

	const appointmentTypeText = cond([
		[equals('hrt_e'), always('Estrogen')],
		[equals('hrt_t'), always('Testosterone')],
		[equals('vc'), always('Primary Care')],
		[equals('mh'), () => getMentalHealthAppointmentText(mhTopic)],
	])(flowId);

	const {
		data: isVisitWaivableData,
		loading: isVisitWaivableLoading,
		waiveVisit,
	} = useMemberIsVisitWaivable(serviceType);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const {
		loading: serviceDurationLoading,
		error: serviceDurationError,
		data: serviceDurationValue,
	} = useQuery<{ authenticatedMember: { defaultVisitDuration: ServiceDuration } }>(
		CURRENT_MEMBER_WITH_DEFAULT_VISIT_DURATION,
		{
			variables: {
				serviceType:
					serviceType === 'VC' && mhTopic === MHTopic.MEDICATION_MANAGEMENT
						? getServiceType(mhTopic, {
								uppercase: true,
						  })
						: serviceType,
				clinicalTopic:
					serviceType === 'VC' && mhTopic === MHTopic.ADHD ? MHTopic.ADHD.toUpperCase() : null,
			},
			client: graphQLv2Client,
		}
	);

	// Effects
	useDeepCompareEffect(() => {
		setNextButtonDisabled(
			scheduledAppointment?.id === undefined ||
				isVisitWaivableLoading ||
				appointmentLoading ||
				!isValidAppointment
		);
	}, [scheduledAppointment, isVisitWaivableLoading, appointmentLoading, isValidAppointment]);

	useEffect(() => {
		if (!state || !zip) {
			setLocationType('none');
			return;
		}

		const getBookingLocationType = async () => {
			const authorizationToken = await getAuthToken();
			if (!includes(flowId, ['hrt_e', 'hrt_t'])) {
				setLocationType('virtual');
				return;
			}

			try {
				const response: AxiosResponse<IGetBookingLocationTypeResponse> = await axios.get(
					stringifyUrl({
						url: `${ApiEndpoints.cogsworth}/get-booking-location-type/${
							flowId === 'hrt_t' ? 'hrt-t' : flowId === 'hrt_e' ? 'hrt-e' : ''
						}`,
						query: {
							state,
							zip,
						},
					}),
					{
						headers: {
							authorizationToken,
						},
						responseType: 'json',
					}
				);
				console.log('booking location response', response);
				setLocationType(response.data.locationType);
			} catch (error: unknown) {
				console.error(error);
			}
		};
		getBookingLocationType();
	}, [addressData, setLocationType]);

	useEffect(() => {
		showAppointmentToast(memberInfo?.stateOfResidence?.abbreviation);
	}, [memberInfo?.stateOfResidence?.abbreviation]);

	// Methods and IFrames
	const resetAppointmentStatus = () => {
		setShouldResetAppointment(true);
	};

	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);
		if (!isPaymentWaived) {
			if (isVisitWaivableData?.isWaivable && scheduledAppointment?.id && serviceType) {
				const waiveVisitResponse = await waiveVisit(
					scheduledAppointment?.id,
					serviceType,
					existingFormData.formUuid as string
				);
				setIsPaymentWaived(waiveVisitResponse.waived);
				if (waiveVisitResponse.waived) {
					await markFlowComplete({
						variables: {
							flowName: flowId,
						},
					});
					if (markCompleteError) {
						console.error(
							`Error marking MH flow complete: ${JSON.stringify(markCompleteError.message)}`
						);
					}
				}
			}
		}
		await handleFormSubmission(
			exists(scheduledAppointment?.id)
				? {
						cogsworthId: scheduledAppointment?.id,
						cogsworthAppointmentTime: scheduledAppointment?.time,
						justScheduled: true,
						...data,
				  }
				: { ...data },
			stepKey
		);
		setSubmitting(false);
	};

	const AppointmentIframe = () => (
		<SpacedGridItem marginBottom="xl">
			<NewAppointmentBooking
				loading={appointmentLoading}
				serviceDuration={
					durationRequiredServiceTypes.includes(flowKey)
						? serviceDurationValue?.authenticatedMember?.defaultVisitDuration
						: undefined
				}
				serviceDurationError={serviceDurationError}
				defaultVisitDuration={serviceDurationValue?.authenticatedMember?.defaultVisitDuration}
				serviceType={flowKey}
				serviceVersion={2}
				isMhm={equals('mhm', getServiceType(mhTopic)) || mhTopic === MHTopic.ADHD}
			/>
		</SpacedGridItem>
	);

	const AppointmentIframeInPerson = ({ locationType }: { locationType: string }) => (
		<SpacedGridItem marginBottom="xl">
			<NewAppointmentBooking
				loading={appointmentLoading}
				serviceType="t"
				locationType={locationType}
				serviceVersion={2}
			/>
		</SpacedGridItem>
	);

	// Views
	const inPersonScheduleAppointmentView = (
		<>
			<SpacedHeader marginBottom="sm" fontSize="sm">
				{pageContent.inPersonHeader}
			</SpacedHeader>
			<SpacedWYSIWYG
				marginBottom="lgxl"
				dangerouslySetInnerHTML={{ __html: pageContent.inPersonDescription }}
			/>
			<AppointmentIframeInPerson locationType={locationType} />
		</>
	);

	const appointmentScheduledView = (
		<FlexColumn gap="sm">
			<HeadingText fontSize="sm">{pageContent.appointmentHeader}</HeadingText>
			<MessageIconCard
				bgColor="cardRed"
				Icon={Clock}
				iconAlt="Clock"
				message={pageContent.appointmentDescription}
			/>
			<SpacedColumn gap="xssm" marginBottom="sm">
				<BasicText variant="small">
					{interpolate(pageContent.appointmentSubheader, { appointmentType: appointmentTypeText })}
				</BasicText>
				<BasicText variant="body">{`${localizedDate} at ${localizedTime}`}</BasicText>
				{isIrlAppointment && (
					<>
						<BasicText variant="fineprintBold">{pageContent.locationSubheader}</BasicText>
						<WYSIWYGContent dangerouslySetInnerHTML={{ __html: pageContent.locationDescription }} />
					</>
				)}
				<TextButton
					label={pageContent.editLabel}
					variant="text"
					underline
					onClick={() => resetAppointmentStatus()}
				/>
			</SpacedColumn>
			<CancellationPolicy defaultOpen={false} label={pageContent.cancellationHeader}>
				<WYSIWYGContent dangerouslySetInnerHTML={{ __html: pageContent.cancellationDescription }} />
			</CancellationPolicy>
			<PaddedSpacedWYSIWYG
				dangerouslySetInnerHTML={{ __html: pageContent.interpreterInfo }}
				textVariant="fineprint"
				paddingLeft="md"
				paddingRight="md"
			/>
		</FlexColumn>
	);

	// Return states
	if (
		memberLoading ||
		addressLoading ||
		isVisitWaivableLoading ||
		serviceDurationLoading ||
		doesNotExist(locationType)
	) {
		// show loading screen while we're determining location type
		return (
			<CenteredGridItem>
				<BookingContainer>
					<Skeleton fullHeight fullWidth structure="single" />
				</BookingContainer>
			</CenteredGridItem>
		);
	}

	if (
		!shouldResetAppointment &&
		or(
			location.state?.isPastCancelledDate,
			all(equals(true), [
				exists(memberInfo?.membershipSubscription?.cancelAt),
				exists(appointmentDate),
				appointmentDate > DateTime.fromISO(memberInfo?.membershipSubscription?.cancelAt ?? ''),
			])
		)
	) {
		return (
			<CenteredGridItem>
				<PastCancelDateAppointmentError
					appointmentType={appointmentTypeText}
					convertedDate={appointmentDate}
					loading={false}
					onClick={() => resetAppointmentStatus()}
					{...pageContent.pastMembershipCancelDate}
				/>
			</CenteredGridItem>
		);
	}

	return (
		<CenteredGridItem>
			<form onSubmit={handleSubmit(onSubmit)}>
				<SpacedGridItem marginBottom="xxl">
					{isAppointmentScheduled ? (
						appointmentScheduledView
					) : isIrlAppointment ? (
						inPersonScheduleAppointmentView
					) : (
						<AppointmentIframe />
					)}
				</SpacedGridItem>
				<SpacedGridItem marginBottom="xxxl">
					<AlignedColumn gap="lg" alignItems="center">
						<SubmitButton
							label={commonLabels.checkout}
							type="submit"
							variant="secondary"
							submitting={submitting}
							labelSize="md"
							disabled={nextButtonDisabled}
						/>
						<TextButton
							label="Back"
							variant="text"
							underline
							onClick={() => handleNavBack(stepKey)}
						/>
					</AlignedColumn>
				</SpacedGridItem>
			</form>
			<Toast
				position="top-center"
				theme="colored"
				pauseOnHover
				pauseOnFocusLoss={false}
				autoClose={7500}
			/>
		</CenteredGridItem>
	);
};

export default MemberAppointmentSelectionStep;
